import {
  getPagedResponseNormaliser,
  lifestyleInformationNormaliser,
  practitionersPatientNormaliser,
  practitionerPatientIdentityCapabilityNormaliser,
  practitionerPatientIdentityValuesNormaliser,
  staticFileNormaliser,
  staticFileOrNullNormaliser,
} from '@common/normalisers'
import {
  DataRequestUrlParams,
  endpointKeys,
  FileUrlParams,
  PatientIdUrlParam,
  PractitionerPatientsUrlParams,
  PractitionerPatientUrlParams,
  CushlaGetQueryOptions,
} from '@common/api'
import {
  DataPermissionsRequest,
  PractitionersPatient,
  PagedResponse,
  LifestyleInformation,
  PractitionerPatientIdentityCapability,
  PractitionerPatientIdentityValues,
  StaticFile,
} from '@common/models'

import {useApi, useWebGet, useWebInfiniteGet, useWebSet} from './helpers'

export const useGetPractitionerPatients = (
  params: PractitionerPatientsUrlParams,
) =>
  useWebGet<PagedResponse<PractitionersPatient>>(
    [endpointKeys.practitionerPatients, params],
    useApi().practitionerPatients(params),
    getPagedResponseNormaliser(practitionersPatientNormaliser),
  )

export const useInfiniteGetPractitionerPatients = (
  params: PractitionerPatientsUrlParams,
) =>
  useWebInfiniteGet<PagedResponse<PractitionersPatient>>(
    [endpointKeys.practitionerPatients, params],
    useApi().practitionerPatients(params),
    getPagedResponseNormaliser(practitionersPatientNormaliser),
  )

export const useGetPractitionerPatient = (
  params: PractitionerPatientUrlParams,
) =>
  useWebGet<PractitionersPatient>(
    [endpointKeys.practitionerPatient, params],
    useApi().practitionerPatient(params),
    practitionersPatientNormaliser,
  )

export const useRequestPatientData = (params: DataRequestUrlParams) =>
  useWebSet<DataPermissionsRequest>(
    [endpointKeys.dataRequest, params],
    useApi().dataRequest(params),
    'POST',
    undefined,
    [[endpointKeys.practitionerPatient], [endpointKeys.practitionerPatients]],
  )

export const useGetPatientLifestyleInformation = (params: PatientIdUrlParam) =>
  useWebGet<LifestyleInformation>(
    [endpointKeys.patientLifestyleInformation, params],
    useApi().patientLifestyleInformation(params),
    lifestyleInformationNormaliser,
  )

export const useGetConsultationFileUrl = (
  params: FileUrlParams,
  options: CushlaGetQueryOptions<StaticFile>,
) =>
  useWebGet<StaticFile>(
    [endpointKeys.consultationFile, params],
    useApi().consultationFile(params),
    staticFileNormaliser,
    {
      gcTime: 0,
      ...options,
    },
  )

export const useGetMedicalRecordFileUrl = (
  params: FileUrlParams,
  options: CushlaGetQueryOptions<StaticFile>,
) =>
  useWebGet<StaticFile>(
    [endpointKeys.medicalRecordFile, params],
    useApi().medicalRecordFile(params),
    staticFileNormaliser,
    {
      gcTime: 0,
      ...options,
    },
  )

export const useGetSourceFileUrl = (
  params: FileUrlParams,
  options: CushlaGetQueryOptions<StaticFile>,
) =>
  useWebGet<StaticFile>(
    [endpointKeys.fileUploadSource, params],
    useApi().fileUploadSource(params),
    staticFileNormaliser,
    {
      gcTime: 0,
      ...options,
    },
  )

export const useGetPatientProfilePicture = (
  params: PatientIdUrlParam,
  options?: CushlaGetQueryOptions<StaticFile | null>,
) =>
  useWebGet<StaticFile | null>(
    [endpointKeys.patientProfilePicture, params],
    useApi().patientProfilePicture(params),
    staticFileOrNullNormaliser,
    options,
  )

export const useGetPractitionerPatientIdentityCapability = (
  params: PractitionerPatientUrlParams,
  options?: CushlaGetQueryOptions<PractitionerPatientIdentityCapability>,
) =>
  useWebGet<PractitionerPatientIdentityCapability>(
    [endpointKeys.practitionerPatientIdentityCapability, params],
    useApi().practitionerPatientIdentityCapability(params),
    practitionerPatientIdentityCapabilityNormaliser,
    options,
  )

export const useUpdatePractitionerPatientIdentityDemographics = (
  params: PractitionerPatientUrlParams,
) =>
  useWebSet<
    PractitionerPatientIdentityValues,
    PractitionerPatientIdentityValues
  >(
    [endpointKeys.practitionerPatientIdentityDemographics, params],
    useApi().practitionerPatientIdentityDemographics(params),
    'PUT',
    practitionerPatientIdentityValuesNormaliser,
    [
      [endpointKeys.practitionerPatientIdentityCapability, params],
      [endpointKeys.practitionerPatient, params],
      [endpointKeys.practitionerPatients],
      [endpointKeys.contactDetails, {patientId: params.patientId}],
    ],
  )
